import { Checkbox, Space, Typography, Table } from 'antd'
import { centsToDollarsDisplay } from '../displayFormatters'
import RichTextEditor from '../RichTextEditor/RichTextEditor'
import { renderPricingType, LineItem as Model, Action, setDescription, setLocked, getGroupedItems, setSelectedItemGuids, setSelectedItemColumns, setShowItems, setAttachments, addAttachment } from './lineItemsModel'
import { useJobItems } from '../Items/itemQueries'
import { ItemClass, ItemClasses, itemClassName } from '../../common/constants'
import './LineItem.css'
import VSpace from '../common/VSpace'
import ProposalAttachments from './ProposalAttachments'

const { Text } = Typography

export type LineItemProps = {
  lineItem: Model,
  jobId: number,
  customerId?: number,
  dispatch: (a: Action) => void,
  redesign?: boolean,
}

export default function LineItem({ lineItem, dispatch, jobId, customerId, redesign = false }: LineItemProps) {
  const itemsQuery = useJobItems(jobId)
  const estimatedItems = (itemsQuery.data?.estimatedItems ?? []).filter(x => lineItem.workareaIds.includes(x.workareaId))
  const groupedItems = getGroupedItems(estimatedItems, itemsQuery.data?.catalogItems ?? [])
  const selectedItemGroupKeys = groupedItems.filter(x => x.itemGuids.some(y => lineItem.selectedItemGuids.includes(y))).map(x => x.key)

  const serverState = itemsQuery.data ?? { estimatedItems: [], catalogItems: [] }

  const handleSelectedItemsChange = (selectedKeys: string[]) => {
    const selectedItemGuids = groupedItems.filter(x => selectedKeys.includes(x.key)).flatMap(x => x.itemGuids)
    dispatch(setSelectedItemGuids(selectedItemGuids))
  }

  const handleShowItemsChange = (checked: boolean) => {
    dispatch(setShowItems(checked, serverState))
    if (checked && lineItem.selectedItemGuids.length === 0) {
      handleSelectedItemsChange(groupedItems.map(x => x.key))
    } else {
      handleSelectedItemsChange([])
    }
  }

  const handleClassChange = (c: ItemClass, checked: boolean) => {
    if (checked) {
      const newSelectedItemGuids = estimatedItems.filter(x => x.class === c).map(x => x.guid)
      const uniqueSelectedItemGuids = Array.from(new Set([...lineItem.selectedItemGuids, ...newSelectedItemGuids]))
      dispatch(setSelectedItemGuids(uniqueSelectedItemGuids))
    } else {
      dispatch(setSelectedItemGuids(lineItem.selectedItemGuids.filter(x => estimatedItems.find(y => y.guid === x)?.class !== c)))
    }
  }

  const classState = (c: ItemClass): { checked: boolean, indeterminate: boolean, disabled: boolean } => {
    const classItems = estimatedItems.filter(x => x.class === c)
    const allChecked = classItems.every(x => lineItem.selectedItemGuids.includes(x.guid))
    return {
      checked: classItems.length > 0 && allChecked,
      indeterminate: !allChecked && classItems.some(x => lineItem.selectedItemGuids.includes(x.guid)),
      disabled: classItems.length === 0
    };
  }

  return <Space direction="vertical" style={{ flexGrow: 1, width: '100%' }} size="large" className="line-item">
    {lineItem.enabled &&
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Space size="small" direction="vertical" style={{ width: '100%' }}>
          Description
          <RichTextEditor
            value={lineItem.description}
            allowImages={redesign}
            onChange={value => dispatch(setDescription(value))}
          />
        </Space>

        {redesign && customerId !== undefined &&
          <ProposalAttachments
            attachments={lineItem.attachments ?? []}
            customerId={customerId}
            jobId={jobId}
            onChange={value => dispatch(setAttachments(value))}
            onAdd={value => dispatch(addAttachment(value))}
            embedded
          />
        }

        <Text>Pricing Type: <em>{renderPricingType(lineItem.pricingType)}</em></Text>

        <Space>
          <Checkbox checked={lineItem.locked} onChange={e => dispatch(setLocked(e.target.checked))}>
            Lock this work area in the client approval screen
          </Checkbox>
        </Space>

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <Checkbox checked={lineItem.showItems} onChange={e => handleShowItemsChange(e.target.checked)}>
              Show items on the proposal
            </Checkbox>
          </Space>
          {lineItem.showItems &&
            <Table
              dataSource={groupedItems}
              bordered
              title={() => <VSpace>
                <Space className="selected-columns">
                  Display Columns:
                  <Checkbox checked disabled>Name</Checkbox>
                  <Checkbox.Group
                    value={lineItem.selectedItemColumns}
                    onChange={columns => dispatch(setSelectedItemColumns(columns))}
                    options={[
                      { label: 'Quantity & Units', value: 'quantityAndUnits' },
                      { label: 'Total Price', value: 'totalPrice' },
                    ]} />
                </Space>
                <Space>
                  Item Classes:
                  <Checkbox {...classState(ItemClasses.Labor)} onChange={e => handleClassChange(ItemClasses.Labor, e.target.checked)}>Labor</Checkbox>
                  <Checkbox {...classState(ItemClasses.Materials)} onChange={e => handleClassChange(ItemClasses.Materials, e.target.checked)}>Materials</Checkbox>
                  <Checkbox {...classState(ItemClasses.Equipment)} onChange={e => handleClassChange(ItemClasses.Equipment, e.target.checked)}>Equipment</Checkbox>
                  <Checkbox {...classState(ItemClasses.Subcontracting)} onChange={e => handleClassChange(ItemClasses.Subcontracting, e.target.checked)}>Subcontractor</Checkbox>
                </Space>
              </VSpace>}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys: selectedItemGroupKeys,
                onChange: keys => handleSelectedItemsChange(keys as string[])
              }}
              rowKey="key"
              columns={[
                { dataIndex: 'name', title: 'Name' },
                {
                  dataIndex: 'class',
                  title: 'Class',
                  render: itemClassName,
                },
                {
                  dataIndex: 'quantity',
                  title: 'Quantity',
                  className: lineItem.selectedItemColumns.includes('quantityAndUnits') ? '' : 'column-excluded',
                  render: (_, item) => <span>{item.quantity} {item.units}</span>
                },
                {
                  dataIndex: 'totalPrice',
                  title: 'Total Price',
                  render: centsToDollarsDisplay,
                  className: lineItem.selectedItemColumns.includes('totalPrice') ? '' : 'column-excluded',
                  align: 'right'
                },
              ]}
              pagination={false}
            />
          }
        </Space>
      </Space>
    }
  </Space>
}
